/**
 * Per-call overrides for messages that are too generic to be useful in a
 * specific context. The defaults here have to make sense anywhere in the
 * add-in, which means they can't say anything about what the user was
 * actually trying to do — so a caller that knows the context can supply
 * something better.
 */
export interface ErrorMessageOverrides {
  /**
   * Shown instead of the generic 403 text. Worth setting anywhere a 403 is
   * an expected, explainable outcome rather than a surprise — e.g. an
   * action gated on a role the user may simply not hold.
   */
  forbidden?: string;
  /**
   * Shown instead of the generic 413 text. Upload limits differ per
   * endpoint (client documents 100MB, discussion attachments 5MB,
   * workbook snapshots 50MB), and this util has no way to know which one
   * the caller hit — so a caller that does should state the actual limit.
   */
  tooLarge?: string;
}

/**
 * Converts technical error messages into user-friendly messages
 * @param error - The error object or message
 * @param overrides - Context-specific replacements, see {@link ErrorMessageOverrides}
 * @returns A user-friendly error message
 */
export function getUserFriendlyErrorMessage(
  error: unknown,
  overrides?: ErrorMessageOverrides,
): string {
  const errorMessage = error instanceof Error ? error.message : String(error);
  const lowerMessage = errorMessage.toLowerCase();

  // MSAL popup errors — surfaced when Entra sign-in can't open its popup
  // (usually browser popup-blocker or the user closing the window before
  // load). Duck-type on errorCode so we don't have to import the MSAL
  // types into this generic util. Placed first so it wins over the
  // generic 401/network branches below.
  if (error && typeof error === 'object' && 'errorCode' in error) {
    const code = (error as any).errorCode;
    if (code === 'popup_window_error' || code === 'empty_window_error') {
      return 'Your browser blocked the sign-in popup. Please allow popups on this site '
           + '(look for the blocked-popup icon on the right of the address bar), '
           + 'then click Retry.';
    }
  }

  // Extract HTTP status code from error object if available
  let statusCode: number | undefined;
  if (error && typeof error === 'object') {
    // Check common status code properties
    statusCode = (error as any).status || (error as any).statusCode || (error as any).code;
  }

  // Check HTTP status codes first (most specific) - use actual status code if available
  // Authentication errors
  if (statusCode === 401 || lowerMessage.includes('unauthorized') || lowerMessage.includes('401')) {
    return 'Authentication failed. Please sign in again.';
  }

  // Permission errors
  if (statusCode === 403 || lowerMessage.includes('forbidden') || lowerMessage.includes('403')) {
    return overrides?.forbidden ?? 'You do not have permission to access this resource.';
  }

  // Not found errors
  if (statusCode === 404 || lowerMessage.includes('not found') || lowerMessage.includes('404')) {
    return 'The requested resource was not found. The spreadsheet may have been deleted.';
  }

  // Payload too large. Kestrel rejects an oversized request body before it
  // ever reaches the controller, so the API never gets to return its own
  // friendly validation message — this is the only place the user can be
  // told what actually happened.
  if (statusCode === 413 ||
      lowerMessage.includes('413') ||
      lowerMessage.includes('payload too large') ||
      lowerMessage.includes('request entity too large')) {
    return overrides?.tooLarge
      ?? 'That file is too large to upload. Try a smaller file.';
  }

  // Server errors - provide more specific messages
  if (statusCode === 500 || lowerMessage.includes('500') || lowerMessage.includes('internal server error')) {
    return 'The API server encountered an error while processing your request. Please try again later.';
  }

  if (statusCode === 502 || lowerMessage.includes('502') || lowerMessage.includes('bad gateway')) {
    return 'The API server is currently unavailable (Bad Gateway). Please try again later.';
  }

  if (statusCode === 503 || lowerMessage.includes('503') || lowerMessage.includes('service unavailable')) {
    return 'The API server is temporarily unavailable. Please try again in a few moments.';
  }

  if (statusCode === 504 || lowerMessage.includes('504') || lowerMessage.includes('gateway timeout')) {
    return 'The request to the API server timed out. Please try again.';
  }

  // Network/connection errors
  if (lowerMessage.includes('failed to fetch') ||
      lowerMessage.includes('network request failed') ||
      lowerMessage.includes('networkerror')) {
    return 'Could not connect to the API server. Please check your internet connection and try again.';
  }

  // Generic timeout errors (check after specific status codes)
  if (lowerMessage.includes('timeout') ||
      lowerMessage.includes('timed out')) {
    return 'The connection to the API server timed out. The server may be busy or unavailable.';
  }

  // CORS errors
  if (lowerMessage.includes('cors')) {
    return 'Unable to connect to the API server due to security restrictions.';
  }

  // If we can't identify the error, return a generic but friendly message
  // Still include the original error for debugging
  return `Unable to initialize the add-in. ${errorMessage}`;
}
